import {useCallback, useState, Dispatch, SetStateAction, useEffect} from 'react';

import {isFunction} from '~ui/utils';

import {localStorageWrapper} from '~ui/services/storage';

import {useLatest} from './use-latest';

export function useLocalStorage<T>(
    key: string,
    initialValue?: T | (() => T),
): [T, Dispatch<SetStateAction<T>>, () => void] {
    const [state, setState] = useState(() => {
        const storageValue = localStorageWrapper.get(key);
        if (storageValue !== null) {
            return storageValue;
        }
        const actualInitialValue =
            initialValue && isFunction(initialValue) ? initialValue() : initialValue;

        if (!actualInitialValue) {
            return;
        }

        localStorageWrapper.set(key, actualInitialValue);
        return actualInitialValue;
    });
    const latestState = useLatest(state);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            const {key: changedKey} = e;

            if (changedKey !== key) return;

            const newValue = localStorageWrapper.get(key);

            setState(newValue);
        };

        window.addEventListener('storage', onStorage);

        return () => window.removeEventListener('storage', onStorage);
    }, [key]);

    const updateValue = useCallback(
        (value: SetStateAction<T>) => {
            const newValue = isFunction(value) ? value(latestState.current) : value;
            setState(newValue);
            localStorageWrapper.set(key, newValue);
        },
        [latestState],
    );

    const removeValue = () => {
        setState(undefined);
        localStorageWrapper.remove(key);
    };

    return [state, updateValue, removeValue];
}
